import * as React from 'react';
import { ArrowUp, CaretDown, FileText, Plus } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import {
  chatBubbleRecipe,
  chatComposerInnerRecipe,
  chatComposerInputRecipe,
  chatComposerRecipe,
  chatPasteBodyRecipe,
  chatPasteNameRecipe,
  chatPasteTileRecipe,
  chatPasteToggleRecipe,
  chatWorkingArrowRecipe,
  chatWorkingRecipe,
  chatWorkingStepRecipe,
  chatWorkingWindowRecipe,
} from '../recipes/chat.recipe';
import { Button } from './Button';
import { Text } from './Text';
import { BRANDS } from './BrandMark';

export interface ChatBubbleProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What the person sent, as they typed or pasted it. */
  text: string;
  /**
   * A message of this many lines or more is a paste: shut, it says
   * `pastedLabel` and its first line, and a tap opens the whole text.
   */
  collapseAt?: number;
  /** What a shut paste is called: "Pasted call". */
  pastedLabel?: string;
}

/**
 * What the person sent in a chat, in a bubble at the right. A pasted block
 * of three lines or more starts shut, as "Pasted call" and its first line,
 * so a long dispatch text does not push the conversation off the screen.
 *
 *   <ChatBubble text={message} pastedLabel="Pasted call" />
 *
 * The other side's words are not a bubble: they are Text on the page.
 */
export const ChatBubble = React.forwardRef<HTMLDivElement, ChatBubbleProps>(
  function ChatBubble(
    { text, collapseAt = 3, pastedLabel = 'Pasted', className, ...props },
    ref
  ) {
    const [open, setOpen] = React.useState(false);
    const lines = text.split('\n');
    const pasted = lines.length >= collapseAt;
    const bodyId = React.useId();
    return (
      <div
        ref={ref}
        className={cn(chatBubbleRecipe({ paste: pasted }), className)}
        {...props}
      >
        {pasted ? (
          <>
            <button
              type="button"
              className={chatPasteToggleRecipe()}
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((was) => !was)}
            >
              <span className={chatPasteTileRecipe()} aria-hidden>
                <FileText />
              </span>
              <span className={chatPasteNameRecipe()}>
                <Text as="span" variant="body-strong" truncate>
                  {pastedLabel}
                </Text>
                <Text as="span" variant="body-sm" tone="muted" truncate>
                  {lines[0]}
                </Text>
              </span>
              <CaretDown aria-hidden />
            </button>
            {open ? (
              <div id={bodyId} className={chatPasteBodyRecipe()}>
                {text}
              </div>
            ) : null}
          </>
        ) : (
          text
        )}
      </div>
    );
  }
);

export interface ChatComposerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** Called with the trimmed text. The composer does not clear itself. */
  onSend: (text: string) => void;
  /** The round + on the left: what can be added besides words. */
  onAdd?: () => void;
  addLabel?: string;
  placeholder?: string;
  /** The box's name for a screen reader. */
  label?: string;
  /** Sending is held while a reply is on its way. Typing still works. */
  sendDisabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

/**
 * The box along the bottom of a chat on a phone: a round + on the left for
 * what can be added, the text, and a round send on the right. The box
 * grows with what is typed, up to four lines. Return sends on a keyboard
 * with a pointer; on a phone, Return is a new line and the button sends.
 *
 *   <ChatComposer value={draft} onChange={setDraft}
 *     onSend={(text) => { submit(text); setDraft(''); }}
 *     onAdd={() => setAdding(true)} placeholder="Paste a call" />
 */
export const ChatComposer = React.forwardRef<HTMLDivElement, ChatComposerProps>(
  function ChatComposer(
    {
      value,
      onChange,
      onSend,
      onAdd,
      addLabel = 'Add',
      placeholder,
      label = 'Message',
      sendDisabled = false,
      inputRef,
      className,
      ...props
    },
    ref
  ) {
    const box = React.useRef<HTMLTextAreaElement | null>(null);
    const setBox = (node: HTMLTextAreaElement | null) => {
      box.current = node;
      if (typeof inputRef === 'function') inputRef(node);
      else if (inputRef)
        (
          inputRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = node;
    };
    // The box's height follows what is in it; the recipe caps it.
    React.useLayoutEffect(() => {
      const node = box.current;
      if (!node) return;
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    }, [value]);

    const canSend = !sendDisabled && value.trim().length > 0;
    const send = () => {
      if (canSend) onSend(value.trim());
    };
    return (
      <div ref={ref} className={cn(chatComposerRecipe(), className)} {...props}>
        <div className={chatComposerInnerRecipe()}>
          {onAdd ? (
            <Button
              variant="wash"
              size="icon"
              pill
              aria-label={addLabel}
              onClick={onAdd}
            >
              <Plus />
            </Button>
          ) : null}
          <textarea
            ref={setBox}
            rows={1}
            value={value}
            placeholder={placeholder}
            aria-label={label}
            className={chatComposerInputRecipe()}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              const pointer =
                typeof window !== 'undefined' &&
                window.matchMedia?.('(pointer: fine)').matches;
              if (event.key === 'Enter' && !event.shiftKey && pointer) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button
            variant="primary"
            size="icon"
            pill
            aria-label="Send"
            disabled={!canSend}
            onClick={send}
          >
            <ArrowUp />
          </Button>
        </div>
      </div>
    );
  }
);

export interface ChatWorkingProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * What the work is, in order: "Reading the text", "Finding the pickup".
   * One shows at a time. The last one stays until the work is done.
   */
  steps: readonly string[];
  /** How long each step shows, in milliseconds. */
  stepMs?: number;
}

/**
 * The line a chat shows while the other side works: the Relay arrow (the
 * site icon) flying up and out of view, and the step it is on. Steps move
 * on by time, not by real progress, so name them as what the work
 * involves, never as done.
 * With reduced motion the arrow stands still and the words still change.
 *
 *   <ChatWorking steps={['Reading the text', 'Finding the pickup']} />
 */
export const ChatWorking = React.forwardRef<HTMLDivElement, ChatWorkingProps>(
  function ChatWorking({ steps, stepMs = 1400, className, ...props }, ref) {
    const [index, setIndex] = React.useState(0);
    const last = Math.max(steps.length - 1, 0);
    React.useEffect(() => {
      if (index >= last) return;
      const timer = window.setTimeout(() => setIndex((i) => i + 1), stepMs);
      return () => window.clearTimeout(timer);
    }, [index, last, stepMs]);
    const step = steps[Math.min(index, last)] ?? '';
    return (
      <div
        ref={ref}
        role="status"
        className={cn(chatWorkingRecipe(), className)}
        {...props}
      >
        <span className={chatWorkingWindowRecipe()} aria-hidden>
          <img
            src={BRANDS.relay.glyph}
            alt=""
            className={chatWorkingArrowRecipe()}
          />
        </span>
        <Text
          key={index}
          as="span"
          variant="body-sm"
          tone="muted"
          className={chatWorkingStepRecipe()}
        >
          {step}
        </Text>
      </div>
    );
  }
);
